import type { Address } from "viem"
import type { Chain } from "viem/chains"
import { isAddressEqual } from "viem"

import type { DisplayName, DisplayType, Name, Pair, TokenAddress, TokenSymbol } from "@/domains/core"

import * as Dates from "./dates"
import { EventEmitter } from "./event-emitter"
import { deepMerge } from "./merge"

export const isEquityPair = (pair: Pair) =>
  isAddressEqual(pair.address as Address, "0x24D4b9aAFa455078c4C172Fb582CC52F974A06E6")

export const clampNumber = (value: number, min: number, max: number) => {
  return Math.max(min, Math.min(value, max))
}

export function toFormattedValueN(number: number, numDigits = 2) {
  return `${number.toLocaleString("en-GB", {
    maximumFractionDigits: numDigits,
    minimumFractionDigits: numDigits,
  })}`
}

export const chainToSlug = (chain: Chain) => {
  // @ts-ignore
  const id = chain.chainId ?? chain.id ?? -1
  switch (id) {
    case 250:
      return "fantom"
    case 8453:
      return "base"
    case 146:
      return "sonic"
    default:
      throw new Error(`Unknown id "${id}" for ${chain.name} chain.`)
  }
}

export const getImageUrl = (token: TokenAddress) => {
  if (!token) return ""
  const slug = "base"
  return `https://cdn.jsdelivr.net/gh/unified-defi/equalizer-tokens@latest/${slug}/tokens/128x128/${token.address.toLowerCase()}.png`
}

export const getImageAltText = (token: TokenSymbol) => {
  if (!token) return ""
  return `${token.symbol} logo`
}

export const getUnknownToken = () => {
  return `/assets/base/unknown-logo.png`
}

/**
 * Gets the displayName or reverts back to name when not exists
 * @param thing
 * @returns
 */
export const displayName = (thing: DisplayName & Name) => {
  return thing.displayName ? thing.displayName : thing.name
}

/**
 * Gets the displayType or reverts back to name when not exists
 * @param thing
 * @returns
 */
export const displayType = (thing: DisplayType & Name) => {
  return thing.displayType ? thing.displayType : thing.name
}

/**
 * Renders the displayName and displayType combined. I would prefer to
 * move away from this approach, and to be explicit when rendering
 * @param pair
 * @returns
 */
export const displayPairFull = (pair: Pair) => {
  return `${displayName(pair)} ${displayType(pair)}`
}

export { cn } from "./cn"
export * from "./contract-risk-level"
export * from "./default-pair-sort"
export * from "./default-token-sort"
export * from "./escape-reg-ex"
export * from "./format"
export * from "./format-token-optimization-value"
export { deepMerge, type DeepPartial } from "./merge"
export * from "./get-native-token-price-from-tokens"
export * from "./json-stringify-defi"
export * from "./shortest-value"

export type { IPublicClientGetter } from "./interfaces"
export { PublicClientGetterAdapter } from "./public-client-getter-adapter"
export { Dates, EventEmitter }
export { IntervalScheduler } from "./interval-scheduler"
export { ContextIntervalScheduler } from "./context-interval-scheduler"
export { OrchestratedQuery, type RateLimitConfig, type ChunkSizeBounds } from "./orchestrated-query"
export { OrchestratedQueryStore } from "./orchestrated-query-store"
export type { IOrchestratedQuerySender } from "./orchestrated-query-interfaces"
export type { RpcCallMetrics, RpcExecutionMetrics } from "./rpc-metrics"
export { RpcMetricsCollector } from "./rpc-metrics"
export type { IRpcStrategy, IChunkedRpcStrategy } from "./rpc-strategy"
export { SequentialChunkedRpcStrategy, SingleRpcStrategy } from "./rpc-strategy"
// export { default as OrchestratedQueryDiagnosticsPage } from "./components/orchestrated-query-diagnostics-page"
export { makeGroupId } from "./make-group-id"

const unixTimestamp = (now = Date.now()): number => {
  return Math.floor(now / 1000)
}

export { unixTimestamp }
